import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom'
import {
  LayoutDashboard,
  Users,
  CalendarCheck,
  ClipboardList,
  CreditCard,
  LogOut,
  User,
  Globe,
  Menu,
  Trees,
  CircleUser,
} from 'lucide-react'
import { useAuthStore } from '@/stores/auth'
import { client } from '@/api/client'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
  SheetDescription,
} from '@/components/ui/sheet'
import { Separator } from '@/components/ui/separator'
import { Breadcrumbs } from '@/components/ui/breadcrumbs'
import { cn } from '@/lib/utils'
import { Footer } from '@/components/Footer'

const navItems = [
  { key: 'dashboard', path: '/dashboard', icon: LayoutDashboard },
  { key: 'children', path: '/children', icon: Users },
  { key: 'bookings', path: '/bookings', icon: CalendarCheck },
  { key: 'medicalHistory', path: '/medical-history', icon: ClipboardList },
  { key: 'payments', path: '/payments', icon: CreditCard },
]

export function DashboardLayout() {
  const { t, i18n } = useTranslation(['common', 'home'])
  const { parent, logout, isAuthenticated, updateParent } = useAuthStore()
  const navigate = useNavigate()
  const location = useLocation()
  const [mobileOpen, setMobileOpen] = useState(false)

  const handleLogout = async () => {
    try {
      await logout()
      navigate('/login')
    } catch (error) {
      // Logout error handling
    }
  }

  const toggleLanguage = async () => {
    const newLang = i18n.language === 'el' ? 'en' : 'el'
    i18n.changeLanguage(newLang)

    // Persist language preference to backend
    if (isAuthenticated) {
      try {
        const response = await client.patch('/profile/', { language_preference: newLang })
        updateParent(response.data)
      } catch (error) {
        // Keep UI change even if API fails
      }
    }
  }

  const isActive = (path: string) => {
    if (path === '/dashboard') return location.pathname === '/dashboard' || location.pathname === '/'
    return location.pathname.startsWith(path)
  }

  return (
    <div className="min-h-screen flex flex-col bg-background">
      {/* Top Navigation */}
      <header className="sticky top-0 z-50 w-full border-b bg-card/95 backdrop-blur supports-[backdrop-filter]:bg-card/80">
        <div className="max-w-7xl mx-auto flex h-16 items-center px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/dashboard" className="flex items-center gap-2 mr-6">
            <img src="/images/logo.png" alt="The Ranch" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1 flex-1">
            {navItems.map(({ key, path, icon: Icon }) => (
              <Link
                key={key}
                to={path}
                className={cn(
                  "flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors",
                  isActive(path)
                    ? "bg-primary/10 text-primary"
                    : "text-muted-foreground hover:bg-accent/10 hover:text-foreground"
                )}
              >
                <Icon className="h-4 w-4" />
                {t(`navigation.${key}`)}
              </Link>
            ))}
          </nav>

          {/* Right side: Make Payment + Language + User Menu */}
          <div className="flex items-center gap-1.5 ml-auto">
            {/* Make Payment Button - desktop only */}
            <Button
              variant="default"
              size="sm"
              className="hidden sm:flex"
              onClick={() => navigate('/payments/checkout')}
            >
              <CreditCard className="h-4 w-4 mr-1" />
              {t('navigation.makePayment')}
            </Button>

            {/* Language Switcher - standalone in nav */}
            <Button
              variant="ghost"
              size="sm"
              onClick={toggleLanguage}
              className="gap-1.5"
              aria-label={`Switch to ${i18n.language === 'el' ? 'English' : 'Greek'}`}
            >
              <Globe className="h-4 w-4" />
              <span className="text-xs font-medium">{i18n.language === 'el' ? 'EN' : 'GR'}</span>
            </Button>

            {/* User dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" size="icon" className="rounded-full">
                  <CircleUser className="h-5 w-5 text-muted-foreground" />
                  <span className="sr-only">User menu</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent className="w-56" align="end" forceMount>
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col space-y-1">
                    <p className="text-sm font-medium">{parent?.first_name} {parent?.last_name}</p>
                    <p className="text-xs text-muted-foreground">{parent?.email}</p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => navigate('/profile/edit')}>
                  <User className="mr-2 h-4 w-4" />
                  {t('navigation.profile')}
                </DropdownMenuItem>
                {/* Mobile-only: Make Payment */}
                <DropdownMenuItem className="sm:hidden" onClick={() => navigate('/payments/checkout')}>
                  <CreditCard className="mr-2 h-4 w-4" />
                  {t('navigation.makePayment')}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={handleLogout} className="text-destructive focus:text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  {t('navigation.logout')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile hamburger */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" className="md:hidden">
                  <Menu className="h-5 w-5" />
                  <span className="sr-only">Menu</span>
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-72">
                <SheetHeader>
                  <SheetTitle className="flex items-center gap-2">
                    <Trees className="h-5 w-5 text-primary" />
                    The Ranch
                  </SheetTitle>
                  <SheetDescription className="sr-only">Navigation menu</SheetDescription>
                </SheetHeader>
                <Separator className="my-4" />
                <nav className="flex flex-col gap-1">
                  {navItems.map(({ key, path, icon: Icon }) => (
                    <Link
                      key={key}
                      to={path}
                      onClick={() => setMobileOpen(false)}
                      className={cn(
                        "flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors",
                        isActive(path)
                          ? "bg-primary/10 text-primary"
                          : "text-muted-foreground hover:bg-accent/10 hover:text-foreground"
                      )}
                    >
                      <Icon className="h-5 w-5" />
                      {t(`navigation.${key}`)}
                    </Link>
                  ))}
                </nav>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <div className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
          <Breadcrumbs />
          <Outlet />
        </div>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  )
}
